import { useState, useEffect } from "react";
import { AttachmentMeta } from "@/indexedDB/types";
import { getMediaAttachmentContentFromLocalDB } from "@/indexedDB/services/attachments";
import useBlobToImageURL from "@/hooks/useBlobToImageURL";
import { useUploadControlState } from "@/zustand/UploadControlState";
import useVideoStream from "./useVideoStream";

interface UseLargeVideoAttachmentOptions {
  enabled?: boolean;
  chatId?: string;
  messageId?: string;
  messageTimestamp?: string;
}

/** Which source is feeding the viewer — diagnostic surface for error reports. */
export type LargeVideoSourceKind = "memory" | "cache" | "stream";

function useLargeVideoAttachment(
  meta: AttachmentMeta,
  eCSK: string,
  options: UseLargeVideoAttachmentOptions = {},
): {
  src: string | null;
  loading: boolean;
  error: Error | null;
  streamError: string | null;
  /** Active source path: sender's in-memory file → local cache → SW stream. */
  sourceKind: LargeVideoSourceKind;
} {
  const [cachedBlob, setCachedBlob] = useState<Blob | null>(null);
  const [cacheChecked, setCacheChecked] = useState(false);

  const { enabled = true, chatId, messageId, messageTimestamp } = options;

  useEffect(() => {
    if (!enabled || !meta.attachmentId) {
      setCachedBlob(null);
      setCacheChecked(false);
      return;
    }

    setCacheChecked(false);

    getMediaAttachmentContentFromLocalDB(meta.attachmentId, "content").then(
      (blob) => {
        setCachedBlob(blob);
        setCacheChecked(true);
      },
      () => {
        setCacheChecked(true);
      },
    );
  }, [enabled, meta.attachmentId]);

  // Preview-while-uploading: on the sender's device the original File is held in
  // memory for the duration of the upload, so we can play it locally instead of
  // streaming our own in-progress upload back from the server. Checked FIRST,
  // ahead of the local cache and streaming. Recipients have no in-memory file,
  // so they fall through to streaming (watch-while-uploading) as before.
  const uploadFile = useUploadControlState((state) =>
    meta.attachmentId ? state.controls[meta.attachmentId]?.file : undefined,
  );

  // Sticky memory source: the send flow unregisters the control the moment the
  // upload completes — for a viewer that is OPEN at that moment, the source
  // would be yanked out from under the playing <video> (a visible reload onto
  // the stream path, and an error where no SW is available). The File object
  // itself stays valid, so once seen, keep serving it for as long as this
  // attachment is being viewed; the next open uses the persisted local cache.
  const [heldUploadFile, setHeldUploadFile] = useState<Blob | null>(null);
  useEffect(() => {
    setHeldUploadFile(null);
  }, [meta.attachmentId]);
  // Release the held File when the viewer closes (`enabled` → false): the
  // dialog component stays mounted forever, so without this the reference
  // would be retained long after close. The next open is served by the
  // persisted local cache — or by the live registry if still uploading.
  useEffect(() => {
    if (!enabled) {
      setHeldUploadFile(null);
    }
  }, [enabled]);
  useEffect(() => {
    if (uploadFile) {
      setHeldUploadFile(uploadFile);
    }
  }, [uploadFile]);
  const memoryFile = uploadFile ?? heldUploadFile;

  // in-memory upload file → local cache → stream
  const localBlob = memoryFile ?? cachedBlob;

  const {
    src: streamSrc,
    loading: streamLoading,
    error,
    streamError,
  } = useVideoStream(meta, eCSK, {
    enabled: enabled && cacheChecked && !localBlob,
    chatId,
    messageId,
    messageTimestamp,
  });

  const blobUrl = useBlobToImageURL(localBlob ?? null);

  const loading = localBlob ? !blobUrl : !cacheChecked || streamLoading;
  const src = localBlob ? blobUrl || null : streamSrc;
  const sourceKind: LargeVideoSourceKind = memoryFile
    ? "memory"
    : cachedBlob
      ? "cache"
      : "stream";

  return { src, loading, error, streamError, sourceKind };
}

export default useLargeVideoAttachment;
